import { WrapUpActions, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import { useAuth } from "@/hooks/authentication";
import { fetchWrapUp } from "@/repository/queryFns";
import { useEffect } from "react";
import { ServerEvent } from "@/common/notification";
import { notificationListener } from "@/hooks/notification";
import { useSummaryGenerationStore } from "@/common/stores/nonpersistingStores/summaryGeneration/summaryGenerationStore";
import {
	WrapUp,
	WrapUpInteractionInfo,
	WrapUpStopped,
	WrapUpEndType,
} from "@/common/models/wrapUp";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { useCallSessionStore } from "@/common/stores/nonpersistingStores/callSession/callSessionStore";
import { CallSessionActions } from "@/common/stores/nonpersistingStores/callSession/types";

export const useWrapUpManager = () => {
	const authContext = useAuth();
	const wrapUpActions = useWrapUpStore((actions: WrapUpActions) => actions);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const stopTrackingSummaryGenerating = useSummaryGenerationStore(
		(state) => state.stopTracking
	);

	useEffect(() => {
		if (!authContext.isLogged) {
			return;
		}

		fetchWrapUp().then(updateWrapUpInfo);
	}, [authContext.isLogged]);

	const updateWrapUpInfo = (wrapUp: WrapUp) => {
		wrapUpActions.update(wrapUp);

		appStoreActions.setCache(CacheKey.WrapUpInteractionInfo, {
			id: wrapUp.mediaId,
			name: wrapUp.interactionInfo?.name,
			contactInformation: wrapUp.interactionInfo?.contactInformation,
			description: wrapUp.interactionInfo?.description,
		} as WrapUpInteractionInfo);

		if (!wrapUp) {
			appStoreActions.removeCaches([
				CacheKey.CurrentWrapUpCodeId,
				CacheKey.CurrentWrapUpCodeName,
			]);
		}
	};

	const updateCallSession = useCallSessionStore(
		(actions: CallSessionActions) => actions.update
	);

	notificationListener(ServerEvent.WrapUpStarted, (wrapUp: WrapUp) => {
		wrapUpActions.update(wrapUp);

		if (wrapUp.isFromCall) {
			updateCallSession(null);
		}

		appStoreActions.setCache(CacheKey.WrapUpInteractionInfo, {
			id: wrapUp?.mediaId,
			name: wrapUp?.interactionInfo?.name,
			contactInformation: wrapUp?.interactionInfo?.contactInformation,
			description: wrapUp?.interactionInfo?.description,
		} as WrapUpInteractionInfo);

		appStoreActions.removeCaches([
			CacheKey.CurrentWrapUpCodeId,
			CacheKey.CurrentWrapUpCodeName,
		]);
	});

	notificationListener(
		ServerEvent.WrapUpStopped,
		(wrapUpStop: WrapUpStopped) => {
			// Wrap-up is over: drop any "summary generating" tracking for this interaction. Safety net for
			// a missed TextSummaryReady (e.g. a SignalR reconnect during the generation window) so the
			// spinner can't get stuck, and full cleanup so session ids don't accumulate in the store.
			if (wrapUpStop.mediaId) {
				stopTrackingSummaryGenerating(wrapUpStop.mediaId);
			}

			if (wrapUpStop.endWrapUpType === WrapUpEndType.ByCallPickUp) {
				wrapUpActions.update({
					endWrapUpType: WrapUpEndType.ByCallPickUp,
				} as WrapUp);
			} else {
				wrapUpActions.update(null);
			}

			appStoreActions.removeCaches([CacheKey.SessionVariableText]);
			appStoreActions.removeCaches([CacheKey.WrapUpInteractionInfo]);

			appStoreActions.removeCaches([
				CacheKey.CurrentWrapUpCodeId,
				CacheKey.CurrentWrapUpCodeName,
			]);
		}
	);
};
